import React from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicyPage.css'; /* shared legal page styling */

const DataDeletionPage = () => {
    return (
        <div className="privacy-page">
            <div className="privacy-container">
                {/* Header */}
                <header className="privacy-header">
                    <Link to="/login" className="privacy-logo">
                        <img src="/logo.png" alt="Blastmate" />
                        <span>Blastmate</span>
                    </Link>
                    <h1>Data Deletion Instructions</h1>
                    <p className="privacy-effective">
                        <strong>Effective Date:</strong> 1 March 2026 &nbsp;|&nbsp;
                        <strong>Last Updated:</strong> 6 March 2026
                    </p>
                </header>

                {/* Content */}
                <div className="privacy-content">
                    {/* Introduction */}
                    <section className="privacy-section">
                        <h2>Overview</h2>
                        <p>
                            At <strong>Blastmate</strong> (operated by Root Source Solutions Pvt Ltd), we respect your right to control
                            your personal data. This page explains how you can request the deletion of your data
                            from our platform, in compliance with applicable data protection regulations and
                            Meta's Platform requirements.
                        </p>
                    </section>

                    {/* What data we store */}
                    <section className="privacy-section">
                        <h2>What Data We Store</h2>
                        <p>When you use Blastmate, we store the following data associated with your account:</p>
                        <ul>
                            <li><strong>Account information:</strong> Business name, email, phone number</li>
                            <li><strong>WhatsApp integration data:</strong> WhatsApp Business Account ID, phone number ID, encrypted access tokens</li>
                            <li><strong>Message data:</strong> Conversations between your business and your customers</li>
                            <li><strong>Customer contacts:</strong> Phone numbers and names of your customers</li>
                            <li><strong>Knowledge base:</strong> Business information you uploaded for AI responses</li>
                            <li><strong>Campaign records:</strong> Broadcast campaign history and delivery statistics</li>
                            <li><strong>Bot configurations:</strong> Automated bot flow settings</li>
                            <li><strong>Payment records:</strong> Subscription and payment history (via Razorpay)</li>
                        </ul>
                    </section>

                    {/* How to request deletion */}
                    <section className="privacy-section">
                        <h2>How to Request Data Deletion</h2>
                        <p>You can request deletion of your data through any of the following methods:</p>

                        <h3>Method 1: Email Request</h3>
                        <p>
                            Send an email to <a href="mailto:privacy@blastmate.in">privacy@blastmate.in</a> with
                            the subject line <strong>"Data Deletion Request"</strong> and include:
                        </p>
                        <ul>
                            <li>Your registered email address</li>
                            <li>Your business name</li>
                            <li>Whether you want full account deletion or deletion of specific data</li>
                        </ul>

                        <h3>Method 2: In-App Request</h3>
                        <p>
                            Log in to your Blastmate dashboard, navigate to <strong>Settings</strong>, and use
                            the account deletion option (when available).
                        </p>
                    </section>

                    {/* What happens */}
                    <section className="privacy-section">
                        <h2>What Happens After a Deletion Request</h2>
                        <p>Once we receive a valid deletion request:</p>
                        <ul>
                            <li>We will verify your identity to prevent unauthorized deletion</li>
                            <li>Your data will be scheduled for deletion within <strong>30 days</strong></li>
                            <li>All client-specific database collections (messages, customers, knowledge base, bot flows, campaigns) will be permanently deleted</li>
                            <li>Your WhatsApp integration tokens will be revoked and deleted</li>
                            <li>Your account login credentials will be removed</li>
                        </ul>
                    </section>

                    {/* Data retention exceptions */}
                    <section className="privacy-section">
                        <h2>Data Retention Exceptions</h2>
                        <p>Certain data may be retained beyond the deletion request as required by law:</p>
                        <ul>
                            <li><strong>Payment records:</strong> Retained for 7 years as required by Indian tax and accounting regulations</li>
                            <li><strong>Audit logs:</strong> Anonymized audit records may be retained for security compliance</li>
                            <li><strong>Legal obligations:</strong> Data required for ongoing legal proceedings or regulatory requirements</li>
                        </ul>
                    </section>

                    {/* Meta/Facebook data */}
                    <section className="privacy-section">
                        <h2>Data Received from Meta (Facebook/WhatsApp)</h2>
                        <p>
                            If you connected your WhatsApp Business Account via Meta's OAuth flow, the following
                            Meta-originated data will also be deleted as part of your deletion request:
                        </p>
                        <ul>
                            <li>WhatsApp Business Account ID and Phone Number ID</li>
                            <li>Encrypted OAuth access tokens</li>
                            <li>Webhook event data associated with your account</li>
                        </ul>
                        <p>
                            Please note: This deletion applies only to data stored on Blastmate's servers.
                            To manage data stored by Meta directly, please visit{' '}
                            <a href="https://www.facebook.com/help/delete_account" target="_blank" rel="noopener noreferrer">
                                Meta's data deletion page
                            </a>.
                        </p>
                    </section>

                    {/* Contact */}
                    <section className="privacy-section">
                        <h2>Contact Us</h2>
                        <p>For data deletion requests or any questions about your data:</p>
                        <div className="privacy-contact-card">
                            <p><strong>Root Source Solutions Pvt Ltd</strong></p>
                            <p>Operating as Blastmate</p>
                            <p>Email: <a href="mailto:privacy@blastmate.in">privacy@blastmate.in</a></p>
                            <p>Website: <a href="https://blastmate.in" target="_blank" rel="noopener noreferrer">blastmate.in</a></p>
                        </div>
                    </section>
                </div>

                {/* Footer */}
                <footer className="privacy-footer">
                    <p>&copy; {new Date().getFullYear()} Root Source Solutions Pvt Ltd. All rights reserved.</p>
                    <div className="privacy-footer-links">
                        <Link to="/privacy-policy">Privacy Policy</Link>
                        <span className="privacy-footer-sep">·</span>
                        <Link to="/terms-of-service">Terms of Service</Link>
                        <span className="privacy-footer-sep">·</span>
                        <Link to="/login">Sign In</Link>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default DataDeletionPage;
